import { useState, useEffect, useRef, memo } from "react";

interface DeferredSignupIframeProps {
  src: string;
  title: string;
  height?: number;
  className?: string;
}

/**
 * DeferredSignupIframe - Lazy loads signup iframe with CLS prevention
 * 
 * Key optimizations:
 * - Defers iframe loading until visible in viewport
 * - Shows skeleton placeholder during load
 * - Reserves exact height to prevent CLS (Cumulative Layout Shift)
 * - Uses contain: strict for better rendering performance
 * - Desktop: loads more eagerly for better UX
 */
function DeferredSignupIframeComponent({ 
  src, 
  title, 
  height = 630,
  className = "" 
}: DeferredSignupIframeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const rIC = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 200));
    const cancelRIC = window.cancelIdleCallback || clearTimeout;
    
    // Minimal delay — just enough to not block FCP, then load ASAP
    const isDesktop = window.innerWidth >= 1024;
    const minDelay = isDesktop ? 0 : 50;

    let idleHandle: number | null = null;
    let observer: IntersectionObserver | null = null;

    // First: wait for initial paint to complete (minimum delay)
    // Then: use requestIdleCallback to load when main thread is free
    // This prevents the signup iframe's ~1.3MB JS from blocking FCP/LCP
    const minDelayTimer = setTimeout(() => {
      // Also check viewport proximity - no point loading if way below fold
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Element is near viewport AND we're past initial paint - load via idle callback
              idleHandle = rIC(() => {
                setShouldLoad(true);
              }, { timeout: 3000 });
              observer?.disconnect();
            }
          });
        },
        {
          rootMargin: isDesktop ? "800px" : "400px",
          threshold: 0.01,
        }
      );

      observer.observe(container);
    }, minDelay);

    return () => {
      clearTimeout(minDelayTimer);
      if (idleHandle !== null) cancelRIC(idleHandle);
      observer?.disconnect();
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`relative w-full rounded-xl overflow-hidden touch-none signup-iframe-wrapper ${className}`}
      style={{
        // Fixed height prevents CLS - this is critical for PageSpeed
        height: `${height}px`,
        minHeight: `${height}px`,
        maxHeight: `${height}px`,
        overflow: "hidden",
        WebkitOverflowScrolling: "auto",
        // Strict containment improves rendering performance
        contain: "strict",
      }}
    >
      {/* Skeleton loader - rendered until iframe loads */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-muted/30 flex flex-col items-center justify-center gap-4 p-8"
          style={{ contain: "content" }}
        >
          <div className="w-full max-w-[300px] space-y-4">
            {/* Form field skeletons - more efficient than animate-pulse */}
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div 
                key={i} 
                className="h-12 skeleton-loading rounded-lg" 
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
            <div className="h-14 bg-primary/20 rounded-lg mt-6" />
          </div>
        </div>
      )}
      
      {shouldLoad && (
        <iframe 
          src={src}
          className={`w-full max-w-[370px] mx-auto transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          height={height + 100}
          width="370"
          title={title}
          aria-label={title}
          style={{
            border: "none",
            overflow: "hidden",
            pointerEvents: "auto",
            display: "block",
          }}
          scrolling="no"
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
}

export const DeferredSignupIframe = memo(DeferredSignupIframeComponent);
export default DeferredSignupIframe;
